import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  ExplanationData,
  ApiResponse,
  saveCalculationWithNotes,
  getTaxiCalculation,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import GreenRatingCalculatorModal from "../../shared/GreenRatingCalculatorModal";
import CalculatorIcon from "../../shared/CalculatorIcon";
import ActualCostCalculator, {
  ActualCostData,
} from "../../shared/ActualCostCalculator";

interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  engine_type: string;
  has_origin_declaration: boolean;
  green_rating_group: string;
  vehicle_weight_over_3500: boolean;
}

interface GreenRatingGroup {
  id: number;
  group_number: number;
  group_display_name: string;
  co2_range_min: number;
  co2_range_max: number;
  discount_amount: number;
  is_active: boolean;
  sort_order: number;
}
interface CalculatorData {
  currency_rates: CurrencyRate[];
  explanations: Record<string, ExplanationData>;
  green_rating_groups?: GreenRatingGroup[];
}
interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}
interface SafetyEquipmentData {
  airbagsCount: number;
  systems: {
    blindSpot: boolean; // מערכת זיהוי כלי רכב ב"שטח מת" - 1.0
    sideCollisionActive: boolean; // מערכת אקטיבית למניעת התנגשות צד ב"שטח מת" - 2.0
    reverseBraking: boolean; // בלימה אוטומטית בנסיעה לאחור - 1.0
    fatigueWarning: boolean; // מערכת התרעה במצב של היסח הדעת הנהג - 1.0
    childAlert: boolean; // מערכת להתרעה על השארת ילדים ברכב - 1.5
    doorOpening: boolean; // מערכת אחזרת פתיחת דלת - 1.5
    adaptiveLighting: boolean; // מערכת תאורה אדפטיבית/שליטה באורות גבוהים - 1.0
    junctionBraking: boolean; // בלימת חירום להתנעה חוצה בצמתים - 1.5
  };
}
const ENGINE_TYPES = [
  "בנזין רגיל",
  "היברידי בנזין",
  "פלאג אין בנזין",
  "דיזל רגיל",
  "היברידי דיזל",
  "פלאג אין דיזל",
  "חשמלי",
] as const;
type EngineType = (typeof ENGINE_TYPES)[number];

// מפתחות השדות לטולטיפס הסבר
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
  engine_type: "taxi_engine_type",
  vehicle_weight_over_3500: "weight_over_3500_personal",
  weight_category: "weight_category",
  green_rating_group: "green_rating",
  road_entry_date: "road_entry_date_personal",
  shipping_time_months: "shipping_time_personal",
  target_consumer_price: "consumer_price_personal",
} as const;

const TaxiCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    engine_type: "",
    has_origin_declaration: false,
    green_rating_group: "17",
    vehicle_weight_over_3500: false,
  });
  const SAFETY_SYSTEMS_SCORES = {
    blindSpot: 1.0,
    sideCollisionActive: 2.0,
    reverseBraking: 1.0,
    fatigueWarning: 1.0,
    childAlert: 1.5,
    doorOpening: 1.5,
    adaptiveLighting: 1.0,
    junctionBraking: 1.5,
  } as const;

  const SAFETY_LEVELS = [
    { level: 0, minScore: 0, minAirbags: 0, discount: 0 },
    { level: 1, minScore: 1, minAirbags: 6, discount: 250 },
    { level: 2, minScore: 2, minAirbags: 6, discount: 600 },
    { level: 3, minScore: 3, minAirbags: 6, discount: 1000 },
    { level: 4, minScore: 4, minAirbags: 6, discount: 1600 },
    { level: 5, minScore: 5, minAirbags: 6, discount: 1850 },
    { level: 6, minScore: 6, minAirbags: 6, discount: 2050 },
    { level: 7, minScore: 8, minAirbags: 6, discount: 2250 },
    { level: 8, minScore: 10, minAirbags: 6, discount: 2400 },
  ] as const;

  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [greenRatingGroups, setGreenRatingGroups] = useState<
    GreenRatingGroup[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [calculationResult, setCalculationResult] = useState<any>(null);
  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);
  const [userNotes, setUserNotes] = useState<string>("");
  const [actualCostData, setActualCostData] = useState<ActualCostData | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showGreenRatingModal, setShowGreenRatingModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [safetyEquipmentData, setSafetyEquipmentData] =
    useState<SafetyEquipmentData>({
      airbagsCount: 0,
      systems: {
        blindSpot: false,
        sideCollisionActive: false,
        reverseBraking: false,
        fatigueWarning: false,
        childAlert: false,
        doorOpening: false,
        adaptiveLighting: false,
        junctionBraking: false,
      },
    });
  // טעינת נתוני מחשבון
  useEffect(() => {
    loadCalculatorData();
  }, []);

  const loadCalculatorData = async () => {
    setIsLoading(true);
    try {
      const response: ApiResponse<CalculatorData> =
        await calculatorService.getCalculatorData();

      if (response.res === "OK" && response.data) {
        setCurrencies(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
        setGreenRatingGroups(response.data.green_rating_groups || []); // הוסף שורה זו
      } else {
        throw new Error(response.msg || "שגיאה בטעינת נתוני המחשבון");
      }
    } catch (error) {
      console.error("שגיאה בטעינה:", error);
      setError("שגיאה בטעינת נתוני המחשבון");
    } finally {
      setIsLoading(false);
    }
  };

  // עדכון שער חליפין אוטומטי למטבע הרכב
  useEffect(() => {
    if (formData.currency_type && currencies.length > 0) {
      const selectedCurrency = currencies.find(
        (c) => c.currency_code === formData.currency_type
      );
      if (selectedCurrency) {
        // המרה לnumber כדי למנוע שגיאת toFixed
        const customsRate = Number(selectedCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          exchange_rate: customsRate.toFixed(4),
          insurance_currency_type: selectedCurrency.currency_code,
          insurance_exchange_rate: customsRate.toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencies]);

  // עדכון שער חליפין אוטומטי למטבע הביטוח
  useEffect(() => {
    if (formData.insurance_currency_type && currencies.length > 0) {
      const selectedInsuranceCurrency = currencies.find(
        (c) => c.currency_code === formData.insurance_currency_type
      );
      if (selectedInsuranceCurrency) {
        // המרה לnumber כדי למנוע שגיאת toFixed
        const insuranceCustomsRate =
          Number(selectedInsuranceCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: insuranceCustomsRate.toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencies]);
  const calculateSafetyScore = (): number => {
    return Object.entries(safetyEquipmentData.systems).reduce(
      (total, [key, isEnabled]) => {
        if (isEnabled) {
          return (
            total +
            SAFETY_SYSTEMS_SCORES[key as keyof typeof SAFETY_SYSTEMS_SCORES]
          );
        }
        return total;
      },
      0
    );
  };

  const calculateSafetyLevel = (): number => {
    const score = calculateSafetyScore();
    const airbagsCount = safetyEquipmentData.airbagsCount;

    // בדיקת דרישות כריות אויר עבור M1 (6 מינימום)
    if (airbagsCount < 6) {
      return 0; // לא עומד בדרישות מינימום
    }

    // מיפוי לפי הניקוד החדש (2026):
    if (score >= 10) return 8;
    if (score >= 8) return 7;
    if (score >= 6) return 6;
    if (score >= 5) return 5;
    if (score >= 4) return 4;
    if (score >= 3) return 3;
    if (score >= 2) return 2;
    if (score >= 1) return 1;

    return 0;
  };

  const calculateSafetyDiscount = (): number => {
    const level = calculateSafetyLevel();
    const levelData = SAFETY_LEVELS.find((l) => l.level === level);
    return levelData?.discount || 0;
  };

  const handleAirbagsChange = (count: number) => {
    const validCount = isNaN(count) ? 0 : Math.max(0, Math.min(20, count));

    setSafetyEquipmentData((prev) => ({
      ...prev,
      airbagsCount: validCount,
    }));
  };

  const handleSafetySystemChange = (
    systemKey: keyof typeof SAFETY_SYSTEMS_SCORES,
    enabled: boolean
  ) => {
    setSafetyEquipmentData((prev) => ({
      ...prev,
      systems: {
        ...prev.systems,
        [systemKey]: enabled,
      },
    }));
  };
  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    const cleanValue = value.replace(/^0+/, "") || "0";
    handleInputChange(field, cleanValue);
  };

  const handleGreenRatingGroupSelect = (groupNumber: number) => {
    handleInputChange("green_rating_group", groupNumber.toString());
    setShowGreenRatingModal(false);
  };

  // בדיקת תאריך ייצור - מונית חייבת להיות בת פחות מ-24 חודשים
  const validateManufacturingDate = (dateString: string): boolean => {
    const manufacturingDate = new Date(dateString);
    const now = new Date();
    const yearsDifference = now.getFullYear() - manufacturingDate.getFullYear();
    const monthsDifference = now.getMonth() - manufacturingDate.getMonth();
    const totalMonthsDiff = yearsDifference * 12 + monthsDifference;
    return totalMonthsDiff <= 24;
  };

  // הגדרת תאריך מינימלי - 24 חודשים אחורה מהיום
  const getMinManufacturingDate = (): string => {
    const today = new Date();
    const twentyFourMonthsAgo = new Date(
      today.getFullYear() - 2,
      today.getMonth(),
      today.getDate()
    );
    return twentyFourMonthsAgo.toISOString().split("T")[0];
  };

  const validateStep1 = () => {
    // בדיקת משקל תחילה
    if (formData.vehicle_weight_over_3500) {
      setError("לא ניתן לייבא מונית עם משקל מעל 3.5 טון לייבוא אישי");
      return false;
    }

    // בדיקת שדות חובה
    if (!formData.manufacturing_date) {
      setError("נא להזין תאריך ייצור");
      return false;
    }

    if (!validateManufacturingDate(formData.manufacturing_date)) {
      setError("מונית חייבת להיות בת פחות מ-24 חודשים");
      return false;
    }

    if (!formData.vehicle_price_foreign) {
      setError("נא להזין ערך מונית");
      return false;
    }

    if (!formData.currency_type) {
      setError("נא לבחור סוג מטבע");
      return false;
    }

    if (formData.shipping_cost_foreign === "") {
      setError("נא להזין עלות הובלה (ניתן להזין 0)");
      return false;
    }

    if (formData.local_expenses_ils === "") {
      setError("נא להזין הוצאות מקומיות (ניתן להזין 0)");
      return false;
    }

    if (!formData.engine_type) {
      setError("נא לבחור סוג מנוע");
      return false;
    }

    return true;
  };

  // const formatCurrency = (amount: number, currency: string = "ILS") => {
  //   return new Intl.NumberFormat("he-IL", {
  //     style: "currency",
  //     currency: currency,
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0,
  //   }).format(amount);
  // };

  const handleNextStep = () => {
    if (currentStep === 1 && !termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }

    if (currentStep === 1 && !validateStep1()) {
      return;
    }

    if (currentStep === 1) {
      // בצע חישוב (ללא שמירה)
      performCalculation();
    }
  };

  const performCalculation = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getTaxiCalculation({
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign) || 0,
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate) || 0,
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) || 0,
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        engine_type: formData.engine_type as EngineType,
        has_origin_declaration: formData.has_origin_declaration,
        green_rating_group:
          formData.engine_type === "חשמלי"
            ? 1
            : formData.green_rating_group
            ? parseInt(formData.green_rating_group)
            : undefined,
      });

      if (response.res === "OK" && response.data) {
        setCalculationResult(response.data);
        setCurrentStep(2);
      } else {
        setError(response.msg || "שגיאה בחישוב");
      }
    } catch (error) {
      console.error("שגיאה בחישוב:", error);
      setError("שגיאה בחישוב המונית");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCalculation = async () => {
    if (!calculationResult) return;

    setIsSaving(true);
    setError("");

    try {
      const saveRequest: SaveCalculationRequest = {
        calculation_type: "taxi_vehicle",
        input_data: calculationResult.input_data,
        result_data: {
          ...calculationResult,
          safety_equipment: safetyEquipmentData,
          safety_score: calculateSafetyScore(),
          safety_level: calculateSafetyLevel(),
          safety_discount: calculateSafetyDiscount(),
          actual_cost_data: actualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => {
          navigate("/enduser/dashboard");
        }, 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("שגיאה בשמירה:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון מיסים למונית",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/enduser/calculators/personal");
    }
  };

  const renderStep1 = () => (
    <MotionCard className="p-8">
      <h2 className="text-xl font-bold mb-6 text-center">חישוב מיסים למונית</h2>
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-calculator"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setError("");
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="terms-calculator"
            className="text-sm text-gray-700 flex-1"
          >
            קראתי ואני מאשר/ת את{" "}
            <Link
              to="/terms-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              תנאי השימוש ומדיניות הפרטיות
            </Link>{" "}
            *
          </label>
        </div>
      </div>
      {/* אזהרת משקל */}
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center mb-2">
          <label className="flex items-center font-medium text-yellow-800">
            משקל המונית מעל 3.5 טון?
            <ExplanationTooltip
              stepKey={STEP_KEYS.vehicle_weight_over_3500}
              explanations={explanations}
            />
          </label>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="weight"
              checked={!formData.vehicle_weight_over_3500}
              onChange={() =>
                handleInputChange("vehicle_weight_over_3500", false)
              }
              className="ml-2"
            />
            עד 3.5 טון
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="weight"
              checked={formData.vehicle_weight_over_3500}
              onChange={() =>
                handleInputChange("vehicle_weight_over_3500", true)
              }
              className="ml-2"
            />
            מעל 3.5 טון
          </label>
        </div>
        {formData.vehicle_weight_over_3500 && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            ⚠️ לא ניתן לייבא מונית מעל 3.5 טון לייבוא אישי
          </div>
        )}
      </div>

      {/* הצהרת מקור */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center mb-2">
          <label className="flex items-center font-medium text-blue-800">
            יש לי הצהרת מקור בחשבון או תעודת מקור
            <ExplanationTooltip
              stepKey={STEP_KEYS.has_origin_declaration}
              explanations={explanations}
            />
          </label>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="origin"
              checked={formData.has_origin_declaration}
              onChange={() => handleInputChange("has_origin_declaration", true)}
              className="ml-2"
            />
            יש הצהרת מקור (פטור ממכס)
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="origin"
              checked={!formData.has_origin_declaration}
              onChange={() =>
                handleInputChange("has_origin_declaration", false)
              }
              className="ml-2"
            />
            אין הצהרת מקור
          </label>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="manufacturing_date"
            className="flex items-center mb-2 font-medium"
          >
            תאריך ייצור המונית (חייב להיות בן פחות מ-24 חודשים){" "}
            <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.manufacturing_date}
              explanations={explanations}
            />
          </label>
          <input
            id="manufacturing_date"
            type="date"
            aria-required="true"
            value={formData.manufacturing_date}
            onChange={(e) =>
              handleInputChange("manufacturing_date", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min={getMinManufacturingDate()}
            max={new Date().toISOString().split("T")[0]}
          />
          {formData.manufacturing_date &&
            !validateManufacturingDate(formData.manufacturing_date) && (
              <div className="mt-1 text-red-600 text-sm">
                ⚠️ מונית חייבת להיות בת פחות מ-24 חודשים
              </div>
            )}
        </div>

        <div>
          <label
            htmlFor="vehicle_price_foreign"
            className="flex items-center mb-2 font-medium"
          >
            ערך המונית במטבע המקור <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.vehicle_price_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="vehicle_price_foreign"
            type="number"
            aria-required="true"
            value={formData.vehicle_price_foreign}
            onChange={(e) =>
              handleNumberInputChange("vehicle_price_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="currency_type"
            className="flex items-center mb-2 font-medium"
          >
            סוג מטבע <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="currency_type"
            aria-required="true"
            value={formData.currency_type}
            onChange={(e) => handleInputChange("currency_type", e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">בחר מטבע</option>
            {currencies.map((currency) => (
              <option
                key={currency.currency_code}
                value={currency.currency_code}
              >
                {currency.currency_name_hebrew} ({currency.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="exchange_rate"
            type="number"
            aria-required="true"
            value={formData.exchange_rate}
            onChange={(e) =>
              handleNumberInputChange("exchange_rate", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.0000"
            min="0"
            step="0.0001"
          />
        </div>

        <div>
          <label
            htmlFor="shipping_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            עלות הובלה במטבע המקור <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.shipping_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="shipping_cost_foreign"
            type="number"
            aria-required="true"
            value={formData.shipping_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("shipping_cost_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            עלות ביטוח במטבע המקור
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_cost_foreign"
            type="number"
            value={formData.insurance_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("insurance_cost_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_currency_type"
            className="flex items-center mb-2 font-medium"
          >
            מטבע ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="insurance_currency_type"
            value={formData.insurance_currency_type}
            onChange={(e) =>
              handleInputChange("insurance_currency_type", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">בחר מטבע ביטוח</option>
            {currencies.map((currency) => (
              <option
                key={currency.currency_code}
                value={currency.currency_code}
              >
                {currency.currency_name_hebrew} ({currency.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="insurance_exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_exchange_rate"
            type="number"
            value={formData.insurance_exchange_rate}
            onChange={(e) =>
              handleNumberInputChange("insurance_exchange_rate", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.0000"
            min="0"
            step="0.0001"
          />
        </div>

        <div>
          <label
            htmlFor="local_expenses_ils"
            className="flex items-center mb-2 font-medium"
          >
            הוצאות מקומיות בשקלים <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.local_expenses_ils}
              explanations={explanations}
            />
          </label>
          <input
            id="local_expenses_ils"
            type="number"
            aria-required="true"
            value={formData.local_expenses_ils}
            onChange={(e) =>
              handleNumberInputChange("local_expenses_ils", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="engine_type"
            className="flex items-center mb-2 font-medium"
          >
            סוג מנוע <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.engine_type}
              explanations={explanations}
            />
          </label>
          <select
            id="engine_type"
            aria-required="true"
            value={formData.engine_type}
            onChange={(e) => handleInputChange("engine_type", e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">בחר סוג מנוע</option>
            {ENGINE_TYPES.map((engineType) => (
              <option key={engineType} value={engineType}>
                {engineType}
              </option>
            ))}
          </select>
        </div>

        {formData.engine_type && formData.engine_type !== "חשמלי" && (
          <div>
            <label className="flex items-center mb-2 font-medium">
              קבוצת ציון ירוק
              <ExplanationTooltip
                stepKey={STEP_KEYS.green_rating_group}
                explanations={explanations}
              />
              <CalculatorIcon
                onClick={() => setShowGreenRatingModal(true)}
                tooltip="למחשבון ציון ירוק"
              />
            </label>
            <select
              value={formData.green_rating_group}
              onChange={(e) =>
                handleInputChange("green_rating_group", e.target.value)
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">בחר קבוצת ציון ירוק</option>
              {greenRatingGroups.length > 0
                ? greenRatingGroups
                    .filter((group: GreenRatingGroup) => group.is_active)
                    .sort(
                      (a: GreenRatingGroup, b: GreenRatingGroup) =>
                        a.group_number - b.group_number
                    )
                    .map((group: GreenRatingGroup) => (
                      <option
                        key={group.group_number}
                        value={group.group_number.toString()}
                      >
                        קבוצה {group.group_display_name}
                      </option>
                    ))
                : // Fallback למקרה שהנתונים לא נטענו
                  Array.from({ length: 17 }, (_, i: number) => {
                    const groupNum = i + 1;
                    let displayName: string;
                    if (groupNum <= 14) {
                      displayName = groupNum.toString();
                    } else if (groupNum === 15) {
                      displayName = "15א";
                    } else if (groupNum === 16) {
                      displayName = "15ב";
                    } else {
                      displayName = "15ג";
                    }
                    return (
                      <option key={groupNum} value={groupNum.toString()}>
                        קבוצה {displayName}
                      </option>
                    );
                  })}
            </select>
            <div className="mt-1 text-sm text-gray-500">
              הנחת ציון ירוק תוגבל לגובה מס הקניה (8%)
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-between mt-8">
        <MotionButton
          onClick={handleGoBack}
          variant="secondary"
          className="px-6 py-3"
        >
          ← חזור
        </MotionButton>

        <MotionButton
          onClick={handleNextStep}
          disabled={isLoading || formData.vehicle_weight_over_3500}
          className="px-6 py-3"
        >
          {isLoading ? "מחשב..." : "חשב מיסים →"}
        </MotionButton>
      </div>
    </MotionCard>
  );

  const renderStep2 = () => {
    if (!calculationResult) return null;

    // שימוש במבנה הנכון מה-API
    const intermediate = calculationResult.intermediate_calculations;
    const finalResults = calculationResult.final_results;
    // const inputData = calculationResult.input_data;
    // const calcInfo = calculationResult.calculation_info;

    // חישוב סך המיסים ושיעור המיסים
    const totalTaxes =
      (intermediate?.customs_fee || 0) +
      (finalResults?.purchase_tax_final || 0) +
      (finalResults?.vat || 0);
    // const taxPercentage = (
    //   (totalTaxes / (finalResults?.final_total || 1)) *
    //   100
    // ).toFixed(1);

    // חיפוש מטבע לתצוגה
    const selectedCurrency = currencies.find(
      (c) => c.currency_code === formData.currency_type
    );

    return (
      <MotionCard className="p-8">
        <h3 className="text-xl font-bold mb-6 text-center">
          📊 תוצאות חישוב - מונית
        </h3>

        {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי" וכרטיס השמירה) */}
        <div ref={printRef} className="space-y-6">
        <TaxYearNotice />
        {/* קטע נתוני הקלט */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <h4 className="font-bold text-lg mb-4 text-blue-800">
            נתוני הקלט שהוזנו:
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">תאריך ייצור:</span>
              <span className="font-medium">
                {new Date(formData.manufacturing_date).toLocaleDateString(
                  "he-IL"
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">ערך מונית:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.vehicle_price_foreign || "0"
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">מטבע:</span>
              <span className="font-medium">
                {selectedCurrency?.currency_name_hebrew} (
                {formData.currency_type})
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">שער חליפין:</span>
              <span className="font-medium">
                {parseFloat(formData.exchange_rate || "0").toFixed(4)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות הובלה:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.shipping_cost_foreign || "0"
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות ביטוח:</span>
              <span className="font-medium">
                {parseFloat(formData.insurance_cost_foreign || "0") > 0
                  ? `${parseFloat(
                      formData.insurance_cost_foreign || "0"
                    ).toLocaleString("he-IL")} ${
                      formData.insurance_currency_type
                    }`
                  : "ללא ביטוח"}
              </span>
            </div>

            {parseFloat(formData.insurance_cost_foreign || "0") > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">שער חליפין ביטוח:</span>
                <span className="font-medium">
                  {parseFloat(formData.insurance_exchange_rate || "0").toFixed(
                    4
                  )}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">הוצאות מקומיות:</span>
              <span className="font-medium">
                {parseFloat(formData.local_expenses_ils || "0") > 0
                  ? `₪${parseFloat(
                      formData.local_expenses_ils || "0"
                    ).toLocaleString("he-IL")}`
                  : "אין הוצאות מקומיות"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">סוג מנוע:</span>
              <span className="font-medium text-blue-600">
                {formData.engine_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">הצהרת מקור:</span>
              <span
                className={`font-medium ${
                  formData.has_origin_declaration
                    ? "text-green-600"
                    : "text-orange-600"
                }`}
              >
                {formData.has_origin_declaration ? "יש הצהרה ✓" : "אין הצהרה"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">משקל מונית:</span>
              <span className="font-medium text-green-600">עד 3.5 טון ✓</span>
            </div>

            {intermediate?.green_rating_info && (
              <div className="flex justify-between">
                <span className="text-gray-600">ציון ירוק:</span>
                <span className="font-medium text-green-600">
                  {intermediate.green_rating_info.group_display_name}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg mb-6 border">
          <h4 className="font-bold text-lg mb-4">תוצאות החישוב:</h4>

          <div className="space-y-4 text-black">
            {/* 1. ערך לצרכי מכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">1. ערך לצרכי מכס:</span>
              <span className="font-bold">
                ש"ח{" "}
                {(intermediate?.total_value_for_customs || 0).toLocaleString(
                  "he-IL"
                )}
              </span>
            </div>

            {/* 2. סך המכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">2. סך המכס:</span>
              <span className="font-bold">
                ש"ח {(intermediate?.customs_fee || 0).toLocaleString("he-IL")}
              </span>
            </div>

            {/* 3. שיעור המכס */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">3. שיעור המכס:</span>
              <span className="font-bold">
                {(intermediate?.customs_fee || 0) > 0 ? "7%" : "0%"}
              </span>
            </div>

            {/* 4. סך מס הקניה */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">4. סך מס הקניה:</span>
              <span className="font-bold">
                ש"ח{" "}
                {(finalResults?.purchase_tax_final || 0).toLocaleString(
                  "he-IL"
                )}
              </span>
            </div>

            {/* 5. שיעור מס הקניה המלא */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">5. שיעור מס הקניה המלא:</span>
              <span className="font-bold">8%</span>
            </div>

            {/* 6. תוספת/הנחת ציון ירוק - רק אם קיים */}
            {intermediate?.green_rating_info && (
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="font-medium">
                  6.{" "}
                  {intermediate.green_rating_info.is_discount
                    ? "הנחת"
                    : "תוספת"}{" "}
                  ציון ירוק:
                </span>
                <span className="font-bold">
                  {intermediate.green_rating_info.is_discount ? "-" : "+"}
                  ש"ח{" "}
                  {Math.abs(
                    intermediate.green_rating_info.applied_adjustment || 0
                  ).toLocaleString("he-IL")}
                  <br />
                  {/* <span className="text-sm text-gray-600">
                    (קבוצה {intermediate.green_rating_info.group_display_name})
                  </span> */}
                </span>
              </div>
            )}

            {/* 7. סך המעמ */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">7. סך המע"מ:</span>
              <span className="font-bold">
                ש"ח {(finalResults?.vat || 0).toLocaleString("he-IL")}
              </span>
            </div>

            {/* 8. שיעור המעמ */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">8. שיעור המע"מ:</span>
              <span className="font-bold">18%</span>
            </div>

            {/* 9. סך המיסים */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">9. סך המיסים:</span>
              <span className="font-bold">
                ש"ח {totalTaxes.toLocaleString("he-IL")}
              </span>
            </div>

            {/* 10. שיעור המיסים */}
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">10. שיעור המיסים:</span>
              <span className="font-bold">
                {(() => {
                  const totalValueForCustoms =
                    intermediate?.total_value_for_customs || 0;
                  if (totalValueForCustoms === 0) return "0%";
                  const taxPercentage =
                    (totalTaxes / totalValueForCustoms) * 100;
                  return `${taxPercentage.toFixed(1)}%`;
                })()}
              </span>
            </div>

            {/* 11. עלות הרכב כולל מיסים */}
            <div className="flex justify-between items-center py-3 border-b-2 border-gray-400 bg-gray-50 px-2 rounded">
              <span className="font-bold text-lg">
                11. עלות הרכב כולל מיסים:
              </span>
              <span className="font-bold text-lg text-green-600">
                ש"ח {(finalResults?.final_total || 0).toLocaleString("he-IL")}
              </span>
            </div>
            {/* מחשבון אבזור בטיחותי - רק עד 3.5 טון */}
            {!formData.vehicle_weight_over_3500 && (
              <MotionCard className="p-6 border-2 border-blue-200 bg-blue-50 mt-6">
                <h3 className="font-bold text-lg mb-4 text-blue-800">
                  🛡️ מחשבון רמת אבזור בטיחותי (נלווה)
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  חישוב רמת אבזור בטיחותי על פי נוהל 03/13 משרד התחבורה - ייתכן
                  ותהיה זכאי להחזר לאחר הטסט
                  <br />
                  <span className="font-semibold">
                    שאל את עמיל המכס שלך לפרטים נוספים
                  </span>
                </p>

                {/* מספר כריות אויר */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">
                    כמה כריות אויר יש ברכב שלך? (נדרש מינימום 6 עבור מונית)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={
                      safetyEquipmentData.airbagsCount === 0
                        ? ""
                        : safetyEquipmentData.airbagsCount
                    }
                    onChange={(e) =>
                      handleAirbagsChange(parseInt(e.target.value) || 0)
                    }
                    placeholder="הכנס מספר כריות אויר"
                    className="w-48 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="mr-2 text-sm text-gray-600">
                    כריות (נדרש מינימום 6)
                  </span>
                  {safetyEquipmentData.airbagsCount < 6 &&
                    safetyEquipmentData.airbagsCount > 0 && (
                      <p className="text-red-600 text-xs mt-1">
                        נדרש מינימום 6 כריות אויר עבור מונית
                      </p>
                    )}
                </div>

                {/* מערכות בטיחות */}
                <div className="space-y-3 mb-4">
                  <h4 className="font-medium text-blue-800">מערכות בטיחות:</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.blindSpot}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "blindSpot",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת זיהוי כלי רכב ב"שטח מת" (1)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={
                          safetyEquipmentData.systems.sideCollisionActive
                        }
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "sideCollisionActive",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת אקטיבית למניעת התנגשות צד ב"שטח מת" (2)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.reverseBraking}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "reverseBraking",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>בלימה אוטומטית בנסיעה לאחור (1)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.fatigueWarning}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "fatigueWarning",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת התרעה במצב של היסח הדעת הנהג (1)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.childAlert}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "childAlert",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת להתרעה על השארת ילדים ברכב (1.5)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.doorOpening}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "doorOpening",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת אחזרת פתיחת דלת (1.5)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.adaptiveLighting}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "adaptiveLighting",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>מערכת תאורה אדפטיבית/שליטה באורות גבוהים (1)</span>
                    </label>

                    <label className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyEquipmentData.systems.junctionBraking}
                        onChange={(e) =>
                          handleSafetySystemChange(
                            "junctionBraking",
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>בלימת חירום להתנעה חוצה בצמתים (1.5)</span>
                    </label>
                  </div>
                </div>

                {/* תוצאות החישוב */}
                <div className="bg-white p-4 rounded-lg border border-blue-300">
                  <h4 className="font-bold text-blue-800 mb-3">
                    תוצאות חישוב אבזור בטיחותי:
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">ניקוד כולל:</span>
                      <div
                        className="text-lg font-bold text-blue-600"
                        translate="no"
                      >
                        {calculateSafetyScore().toFixed(1)}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">רמת אבזור:</span>
                      <div
                        className="text-lg font-bold text-blue-600"
                        translate="no"
                      >
                        {calculateSafetyLevel()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">סכום הנחה:</span>
                      <div
                        className="text-lg font-bold text-green-600"
                        translate="no"
                      >
                        {calculateSafetyDiscount().toLocaleString()} ש"ח
                      </div>
                    </div>
                  </div>

                  {safetyEquipmentData.airbagsCount < 6 &&
                    safetyEquipmentData.airbagsCount > 0 && (
                      <p className="text-orange-600 text-xs mt-1">
                        ⚠️ נדרש מינימום 6 כריות אויר עבור מונית לקבלת רמת אבזור
                      </p>
                    )}
                  {safetyEquipmentData.airbagsCount === 0 && (
                    <p className="text-gray-500 text-xs mt-1">
                      📝 אנא הכנס את מספר כריות האויר ברכב
                    </p>
                  )}

                  <div className="mt-3 text-xs text-blue-600">
                    * ייתכן ותהיה זכאי לאחר הטסט להחזר בגין אבזור בטיחותי (שאל
                    את עמיל המכס שלך)
                    <br />* חישוב נלווה בלבד - לא משפיע על עלות הייבוא הסופית
                  </div>
                </div>
              </MotionCard>
            )}
          </div>
        </div>

        {/* דייק לי את התוצאה */}
        <ActualCostCalculator
          vehicleCurrency={formData.currency_type}
          vehicleExchangeRate={formData.exchange_rate}
          vehiclePriceForeign={formData.vehicle_price_foreign}
          shippingCostForeign={formData.shipping_cost_foreign}
          insuranceCurrency={formData.insurance_currency_type}
          insuranceExchangeRate={formData.insurance_exchange_rate}
          insuranceCostForeign={formData.insurance_cost_foreign}
          localExpensesIls={formData.local_expenses_ils}
          // totalTaxes={(() => {
          //   if (!calculationResult) return 0;
          //   const customs =
          //     calculationResult.base_calculations?.customs_fee || 0;
          //   const purchaseTax =
          //     calculationResult.tax_calculation?.final_purchase_tax || 0;
          //   const vat = calculationResult.tax_calculation?.vat_amount || 0;
          //   return customs + purchaseTax + vat;
          // })()}
          totalTaxes={totalTaxes - calculateSafetyDiscount()}
          onDataChange={setActualCostData}
          explanations={explanations}
          initialData={actualCostData}
        />
        </div>
        {/* סוף אזור ההדפסה */}

        <div className="space-y-4 print-exclude">
          <div>
            <label className="block mb-2 font-medium">
              הערות (אופציונלי - עד 1000 תווים):
            </label>
            <textarea
              value={userNotes}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setUserNotes(e.target.value);
                }
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="הוסף הערות או הסברים נוספים לחישוב זה..."
            />
            <div className="text-sm text-gray-500 mt-1">
              {userNotes.length}/1000 תווים
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {isSaved && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              ✅ החישוב נשמר בהצלחה! מעביר לדף הבית...
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-8">
          <MotionButton
            onClick={handleGoBack}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            ← חזור לעריכה
          </MotionButton>

          <MotionButton
            onClick={handlePrint}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200"
          >
            🖨️ הדפס
          </MotionButton>

          <MotionButton
            onClick={handleSaveCalculation}
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            {isSaving ? "שומר..." : isSaved ? "נשמר ✓" : "שמור וחזור לדף הבית"}
          </MotionButton>
        </div>
      </MotionCard>
    );
  };

  if (isLoading && !calculationResult) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>טוען נתוני מחשבון...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון מיסים למונית
          </h1>
        </div>

        {/* אינדיקטור התקדמות - מימין לשמאל */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4" dir="rtl">
            {[1, 2].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {step}
                </div>
                {step < 2 && (
                  <div
                    className={`h-1 w-16 ${
                      currentStep > step ? "bg-blue-600" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>

      {/* מחשבון ציון ירוק */}
      <GreenRatingCalculatorModal
        isOpen={showGreenRatingModal}
        onClose={() => setShowGreenRatingModal(false)}
        onSelectGroup={handleGreenRatingGroupSelect}
        currentGroupNumber={parseInt(formData.green_rating_group) || undefined}
      />
    </div>
  );
};

export default TaxiCalculatorPage;
